import Decimal from "decimal.js";
import type { RunTimeFormat } from "../schemas/items";

const DEFAULT_RUN_TIME_FORMAT: RunTimeFormat = "HOURS_PER_PIECE";
const DEFAULT_QUANTITY_PER = "1";

/**
 * Resolves an operation runtime format, treating format-less legacy values as
 * hours per piece.
 * @param format - Persisted runtime format
 * @returns The explicit runtime format used for conversion
 */
export function resolveRunTimeFormat(
  format: RunTimeFormat | null | undefined,
): RunTimeFormat {
  return format ?? DEFAULT_RUN_TIME_FORMAT;
}

/**
 * Converts a stored operation runtime value to hours per parent item for
 * costing without changing the stored value.
 * @param runTimeValue - Runtime value expressed in the selected format
 * @param format - Runtime value format; legacy missing values are hours per piece
 * @param quantityPerValue - Production-basis quantity required per parent item
 * @returns Hours per parent item, or null when the inputs are invalid
 */
export function getRunTimeHours(
  runTimeValue: string,
  format: RunTimeFormat | null | undefined,
  quantityPerValue: string = DEFAULT_QUANTITY_PER,
): string | null {
  const runTime = parseDecimal(runTimeValue);
  const quantityPer = parseDecimal(quantityPerValue);
  if (
    !runTime ||
    runTime.isNegative() ||
    !quantityPer ||
    quantityPer.lte(0)
  ) {
    return null;
  }
  if (runTime.isZero()) {
    return "0";
  }

  const resolvedFormat = resolveRunTimeFormat(format);
  if (resolvedFormat === "PIECES_PER_HOUR") {
    return quantityPer.div(runTime).toString();
  }
  if (resolvedFormat === "MINUTES_PER_PIECE") {
    return quantityPer.times(runTime).div(60).toString();
  }
  return quantityPer.times(runTime).toString();
}

/**
 * Converts a runtime value between display or ERP formats while preserving
 * its duration for the same production basis.
 * @param runTimeValue - Runtime value expressed in the source format
 * @param sourceFormat - Current runtime format
 * @param targetFormat - Runtime format to convert to
 * @param quantityPerValue - Production-basis quantity required per parent item
 * @returns The value in the target format, or null when the inputs are invalid
 */
export function convertRunTimeValue(
  runTimeValue: string,
  sourceFormat: RunTimeFormat | null | undefined,
  targetFormat: RunTimeFormat,
  quantityPerValue: string = DEFAULT_QUANTITY_PER,
): string | null {
  const hoursValue = getRunTimeHours(
    runTimeValue,
    sourceFormat,
    quantityPerValue,
  );
  if (hoursValue === null) {
    return null;
  }

  return getRunTimeValue(hoursValue, targetFormat, quantityPerValue);
}

function getRunTimeValue(
  hoursValue: string,
  format: RunTimeFormat,
  quantityPerValue: string,
): string | null {
  const hours = parseDecimal(hoursValue);
  const quantityPer = parseDecimal(quantityPerValue);
  if (!hours || hours.isNegative() || !quantityPer || quantityPer.lte(0)) {
    return null;
  }
  if (format === "PIECES_PER_HOUR") {
    return hours.isZero() ? null : quantityPer.div(hours).toString();
  }
  if (format === "MINUTES_PER_PIECE") {
    return hours.times(60).div(quantityPer).toString();
  }
  return hours.div(quantityPer).toString();
}

function parseDecimal(value: string): Decimal | null {
  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() ? decimal : null;
  } catch {
    return null;
  }
}
